import {
  ComponentPropsWithoutRef,
  FocusEventHandler,
  forwardRef,
  ReactElement,
  useState,
} from "react";
import styles from "./Radio.module.scss";
import cx from "classnames";
import { LayoutUtilProps, FieldLabelProps } from "../../types";
import { useLayoutPropsUtil } from "../../hooks";
import { SrOnly } from "../SrOnly";
import { Label } from "../Label";

/**
 * Props for the RadioGroup component
 * @extends ComponentPropsWithoutRef<"fieldset">
 * @extends LayoutUtilProps
 * @extends Omit<FieldLabelProps, "labelProps">
 */
export type RadioGroupProps = ComponentPropsWithoutRef<"fieldset"> &
  LayoutUtilProps &
  Omit<FieldLabelProps, "labelProps"> & {
    /**
     * Required flag for label
     * @remarks This does not enforce error when nothing is selected
     */
    required?: boolean;

    /**
     * Legend for the fieldset
     */
    legend?: string | ReactElement;
  };

/**
 * RadioGroup component for grouping related radio options.
 *
 * Features:
 * - Groups multiple radio options together
 * - Legend support for group labeling
 * - Required field indication
 * - Accessibility support with proper fieldset semantics
 * - Layout utility props for positioning and spacing
 * - Focus and blur event handling
 * - More info support for additional context
 * - Automatic alignment based on flex direction
 *
 * @example
 * <RadioGroup legend="Choose your preference" required>
 *   <Radio label="Option A" value="a" />
 *   <Radio label="Option B" value="b" />
 *   <Radio label="Option C" value="c" />
 * </RadioGroup>
 *
 * @example
 * <RadioGroup
 *   legend="Horizontal layout"
 *   flexDirection="row"
 *   gap="4"
 * >
 *   <Radio label="Yes" value="yes" />
 *   <Radio label="No" value="no" />
 * </RadioGroup>
 */
export const RadioGroup = forwardRef<HTMLFieldSetElement, RadioGroupProps>(
  (props, ref) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      className,
      children,
      legend,
      moreInfo,
      onBlur,
      onFocus,
      required,
      style,
      ...rest
    } = componentProps;

    const RadioGroupClassNames = cx(styles["radio-group"], className);
    const [openInfo, setOpenInfo] = useState<boolean>(false);

    const styleCombined = {
      ...style,
      ...layoutStyles,
      alignItems:
        layoutStyles?.alignItems ??
        (layoutStyles?.flexDirection === "row" ? "center" : undefined),
    };

    const focusHandler: FocusEventHandler<HTMLFieldSetElement> = (e) => {
      onFocus?.(e);
      setOpenInfo(true);
    };

    const blurHandler: FocusEventHandler<HTMLFieldSetElement> = (e) => {
      onBlur?.(e);
      setOpenInfo(false);
    };

    return (
      <fieldset
        role="radiogroup"
        data-anv="radio-group"
        className={RadioGroupClassNames}
        aria-required={required}
        style={styleCombined}
        ref={ref}
        onFocus={focusHandler}
        onBlur={blurHandler}
        {...rest}
      >
        <legend className={styles["legend"]}>
          {legend}
          {required && ", required"}
          {moreInfo && <SrOnly>{moreInfo}</SrOnly>}
        </legend>
        <Label
          aria-hidden
          moreInfo={moreInfo}
          required={required}
          openMoreInfo={openInfo}
        >
          {legend}
        </Label>
        <div
          className={styles["children"]}
          style={{
            flexDirection: layoutStyles?.flexDirection,
            gap: layoutStyles?.gap,
          }}
        >
          {children}
        </div>
      </fieldset>
    );
  },
);

RadioGroup.displayName = "RadioGroup";
